import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to fetch meditation content from the database
 * Falls back to provided default content if API fails
 */
export const useMeditationContent = (contentId, defaultSteps) => {
    const [steps, setSteps] = useState(defaultSteps);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await fetch(`${API_URL}/api/meditations/${contentId}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.steps && data.steps.length > 0) {
                        // Transform database format to component format
                        const transformedSteps = data.steps.map(step => ({
                            id: step.id,
                            // Body Scan uses bodyPart
                            bodyPart: step.body_part || '',
                            // PMR uses muscleGroup
                            muscleGroup: step.muscle_group || '',
                            duration: step.duration || 20,
                            // Body Scan uses instruction
                            instruction: step.instruction || step.release_instruction || '',
                            // PMR uses tensionInstruction and releaseInstruction
                            tensionInstruction: step.tension_instruction || null,
                            releaseInstruction: step.release_instruction || '',
                            guidance: step.guidance || ''
                        }));
                        setSteps(transformedSteps);
                    }
                }
            } catch (err) {
                console.log('Using default content, API error:', err.message);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [contentId]);

    return { steps, loading, error };
};

/**
 * Hook to fetch guided meditation content (script-based)
 */
export const useGuidedMeditationContent = (defaultMeditations) => {
    const [meditations, setMeditations] = useState(defaultMeditations);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await fetch(`${API_URL}/api/meditations?content_type=guided_meditation`);
                if (response.ok) {
                    const data = await response.json();
                    if (data && data.length > 0) {
                        const transformedMeditations = data.map(med => ({
                            id: med.content_id,
                            name: med.name,
                            description: med.description,
                            duration: med.duration,
                            icon: med.icon,
                            color: med.color,
                            bgColor: med.bg_color,
                            script: med.script || []
                        }));
                        setMeditations(transformedMeditations);
                    }
                }
            } catch (err) {
                console.log('Using default meditations, API error:', err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [defaultMeditations]);

    return { meditations, loading };
};
